import { ErrorReportResponse, SettingsDataSource } from "../datasource/settings";
import { DeepPartial, State, StateDataSource } from "../datasource/state";
import { ListModel, SettingsModel, StateModel } from "../model";
import { ListDataSource, ListItem } from "../datasource/list";
import { FilterValue } from "../../lib/filter";
import { ProductId, ItemId, Serializer } from "../datasource";
import { Settings } from "../../lib/timer";

export class CommonListModel<I extends ItemId> extends ListModel<I> {
	constructor(datasource: ListDataSource<I>) {
		super(datasource);
	}

	itemIdSerializer(): Serializer<I> {
		return this.datasource.itemIdSerializer;
	}

	getItemList(): Promise<ListItem<I>[]> {
		return this.datasource.getItemList();
	}

	getItemIdMap(items?: ListItem<I>[]): Promise<Record<string, number>> {
		return (items ?
			Promise.resolve(items) :
			this.datasource.getItemList()
		).then(items => {
			return items.reduce((result, item, index) => ({
				...result,
				[this.itemIdSerializer().toString(item.id)]: index
			}), {});
		});
	}

	getItemIndexMap(items?: ListItem<I>[]): Promise<string[]> {
		return (items ?
			Promise.resolve(items) :
			this.datasource.getItemList()
		).then(items => {
			return items.map((item: ListItem<I>) => this.itemIdSerializer().toString(item.id))
		});
	}

	getFilteredItems(items: ListItem<I>[], filters: FilterValue[]): Promise<ListItem<I>[]> {
		return this.datasource.getFilteredItems(items, filters);
	}

	setAttributes(id: I, data: Partial<ListItem<I>>): void {
		return this.datasource.setAttributes(id, data);
	}
}

export class CommonSettingsModel<P extends ProductId> extends SettingsModel<P> {
	constructor(datasource: SettingsDataSource<P>) {
		super(datasource);
	}

	getKeyboardShortcuts(): boolean {
		return this.datasource.getKeyboardShortcuts();
	}

	setKeyboardShortcuts(enabled: boolean): void {
		this.datasource.setKeyboardShortcuts(enabled);
	}

	getFontSize(): number {
		return this.datasource.getFontSize();
	}

	setFontSize(size: number): void {
		this.datasource.setFontSize(size);
	}

	getRandomize(): boolean {
		return this.datasource.getRandomize();
	}

	setRandomize(randomize: boolean): void {
		this.datasource.setRandomize(randomize);
	}

	toggleTimer(): boolean {
		return this.datasource.toggleTimer();
	}

	getEmailSuggestion(email: string): Promise<string | undefined> {
		return this.datasource.getEmailSuggestion(email);
	}

	sendErrorReport(product: P, form: HTMLFormElement): Promise<ErrorReportResponse> {
		return this.datasource.sendErrorReport(product, form);
	}

	appendError(error: Error | any): void {
		return this.datasource.appendError(error);
	}

	getErrors(): (Error | any)[] {
		return this.datasource.getErrors();
	}

	clearErrors(): void {
		return this.datasource.clearErrors();
	}

	getLocalStorage(): Record<string, any> {
		return this.datasource.getLocalStorage(this.datasource.getLocalStoragePrefix());
	}
}

export class CommonStateModel<S extends State> extends StateModel<S> {
	constructor(datasource: StateDataSource<S>) {
		super(datasource);
	}

	getInitialState(config: Settings): Promise<S> {
		return this.datasource.getInitialState(config);
	}

	setState(state: Omit<S, 'timestamp'> & Partial<Pick<S, 'timestamp'>>): Promise<void> {
		return this.datasource.setState(state);
	}

	updateState(state: DeepPartial<S>): Promise<void> {
		return this.datasource.updateState(state);
	}
}